// src/components/resources/tools/generators/shared/usePreviewDeferral.ts

import {
  useDeferredValue,
  useEffect,
  useLayoutEffect,
  useRef,
  type MutableRefObject,
} from 'react'

/**
 * `useLayoutEffect` on the client, `useEffect` on the server. The staleness flag
 * must commit from a LAYOUT effect (pre-paint, synchronous in the commit phase) so
 * the browser can't process a Download click before it lands — see
 * {@link useCommitStaleRef}. But these generators are `"use client"` components
 * that still PRERENDER (their routes are in the sitemap content-fingerprint
 * manifest), and `useLayoutEffect` warns "does nothing on the server" during that
 * prerender; falling back to `useEffect` server-side (where it's a no-op anyway —
 * the ref is only read client-side) keeps the build warning-free.
 */
const useIsomorphicLayoutEffect = typeof document !== 'undefined' ? useLayoutEffect : useEffect

/**
 * Defer a value that drives a heavy generator preview, and report whether the
 * deferred snapshot currently trails live state.
 *
 * The deferral is what shortens the keystroke's critical path: controls read the
 * live `value`, the memoized preview element reads `deferred`, so the heavy
 * preview re-renders once at low priority instead of synchronously on every
 * keystroke (see the generators' `useMemo(() => <Preview/>)` boundaries).
 *
 * `isStale` is a pure derived render value. Pair this with {@link useStaleRef} /
 * {@link useCommitStaleRef}, which commit the flag from a LAYOUT effect so an
 * export gate (`waitForStable(() => !ref.current)`) can poll it without racing the
 * gap a passive effect leaves between a keystroke's paint and the flag landing
 * (#336). See {@link useCommitStaleRef} for the timing rationale.
 *
 * @returns the deferred snapshot and whether it trails live `value`.
 */
export function usePreviewDeferral<T>(value: T): { deferred: T; isStale: boolean } {
  const deferred = useDeferredValue(value)
  return { deferred, isStale: !Object.is(deferred, value) }
}

/**
 * Commit a preview-staleness flag into a caller-provided ref from a LAYOUT effect,
 * so `ref.current` reflects the just-committed preview BEFORE the browser paints
 * (and so before it can process a Download click).
 *
 * The export gate polls `waitForStable(() => !ref.current)` across rAF frames. The
 * original committed this flag from a PASSIVE effect, which runs AFTER paint — so a
 * "type then immediately Download" on a throttled device could land in the
 * paint→passive-effect gap, read the previous `false`, and green-light a capture
 * of the not-yet-converged preview (#336). A layout effect runs
 * synchronously in the commit phase (pre-paint), closing that gap.
 *
 * A layout effect (like the passive one before it) fires ONLY for COMMITTED
 * renders — never for a low-priority deferred render React started but abandoned —
 * so the `false` edge still can't be written while the DOM shows the previous
 * preview (the AdMockup P2 hazard). That guard is why this lives in an effect and
 * not in render (`react-hooks/refs` also forbids writing a ref during render).
 *
 * Shared by {@link useStaleRef} and the generator deferred-state providers
 * (FakeChat/AiChat), which write into a ref owned by their parent, so the commit
 * semantics live in exactly one place and can't drift between copies. For
 * multi-stream previews, OR the individual `isStale` flags at the call site and
 * pass the combined result (single writer per ref) so the ref waits for every
 * stream to converge before an export proceeds.
 *
 * @param isStale whether the (combined) deferred preview currently trails live.
 * @param ref the (optional) ref to receive the staleness flag.
 */
export function useCommitStaleRef(isStale: boolean, ref?: MutableRefObject<boolean>): void {
  useIsomorphicLayoutEffect(() => {
    if (ref) ref.current = isStale
  }, [isStale, ref])
}

export function useStaleRef(isStale: boolean): MutableRefObject<boolean> {
  const staleRef = useRef(false)
  useCommitStaleRef(isStale, staleRef)
  return staleRef
}
